import React, { Suspense, useMemo } from 'react';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import { Drawer as MuiDrawer, DrawerProps, IconButton } from '@mui/material';
import { Box } from '@mui/system';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Outlet, useNavigate } from 'react-router-dom';
import {  GenericTabsMenu } from 'components/tabs';
import { EcoSpinner } from 'components/icons';
import { useGenericViewerOpened } from 'hooks/useGenericViewerOpened';
import { useUrlParamsValueToString } from 'hooks';
import { useRouteMenu } from 'hooks/routes/useRouteMenu';

const drawerWidth = 480;

export const GenericViewer = (props: GenericViewerProps) => {

    const urlParamsToString = useUrlParamsValueToString();

    const navigate = useNavigate();
    const menus = useRouteMenu(props.viewerUrl || '');

    const openedByUrl = useGenericViewerOpened(props.viewerUrl || '')

    const open = useMemo(() => props.open !== undefined ? props.open : openedByUrl, [props.open, openedByUrl])

    const openDrawer = () => {
        props.openUrl && navigate(`${props.openUrl}?${urlParamsToString}`)
    }

    const closeDrawer = () => {
        navigate(`${props.closeUrl}?${urlParamsToString}`)
    }

    const handleToggleDrawer = () => {

        if(props.open !== undefined) {
            return props.onToggle?.()
        }

        if (open) {
            return closeDrawer()
        }

        return openDrawer()
    }

    const handleDrawerClose = () => {
        if (props.open === undefined) {
            navigate(props.closeUrl || '/')
        }
    }

    const context = useMemo((): GenericViewerContext => {
        return {
            isInsideViewer: true,
            closeDrawer
        }
    }, [closeDrawer])

    return (
        <React.Fragment>
            <Drawer
                sx={{
                    maxWidth: drawerWidth,
                    position: 'fixed',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    zIndex: 9999,
                    overflow: 'hidden'
                }}
                anchor='right'
                variant='permanent'
                open={open}
                onClose={handleDrawerClose}
            >
                <Box
                    width='100%'
                    height='100%'
                    display='flex'
                    flexDirection='row'
                >
                    <Box
                        flex={0.1}
                        style={{
                            border: '1px solid #dadada',
                        }}
                        textAlign='center'
                        alignItems='center'
                        justifyContent='flex-start'
                        display='flex'
                        flexDirection='column'
                    >
                        <IconButton
                            style={{ flex: 1 }}
                            disableRipple
                            onClick={handleToggleDrawer}
                        >
                            {!open && (
                                <ChevronLeftIcon
                                    fontSize='small'
                                    sx={{ color: 'primary.main' }}
                                />
                            )}

                            {open && (
                                <ChevronRightIcon
                                    fontSize='small'
                                    sx={{ color: 'primary.main' }}
                                />
                            )}
                        </IconButton>
                    </Box>
                    <Box
                        flex={0.9}
                        maxWidth={drawerWidth}
                        width='100%'
                        height='100%'
                        pl={2}
                        className="custom-scroll"
                        style={{ overflowX: 'hidden', overflowY: 'auto' }}>

                        {
                            (menus && menus?.length > 0) &&
                            <>
                                <Box mt={2} position="sticky" top="0" bgcolor="primary.light">
                                    <GenericTabsMenu
                                        menus={menus}
                                        path={window.location.pathname}
                                        viewerUrl={props.viewerUrl}
                                        />
                                </Box>

                                <Box >
                                    <Suspense
                                        fallback={
                                            <EcoSpinner style={{ fontSize: 64 }} />
                                        }>
                                        <Outlet context={context} />
                                    </Suspense>
                                </Box>
                            </>
                        }

                        {
                            !(menus && menus?.length > 0) &&
                            <Outlet context={context} />
                        }

                        {
                            props.children
                        }
                        
                    </Box>
                </Box>
            </Drawer>
        </React.Fragment>
    );
};

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    position: 'inherit',
    border: 'none',
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    boxShadow: '3',
});

const closedMixin = (theme: Theme): CSSObject => ({
    position: 'inherit',
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: 40,
    boxShadow: '3',
});

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',

    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));

export type GenericViewerProps = DrawerProps & {
    /**
     * Url to search inside route declaration
     */
    viewerUrl?: string;
    /**
     * Url to close drawer
     */
    closeUrl?: string;
    /**
     * Url to open the viewer
     */
    openUrl?: string ;
    /**
     * This variable give you the hand to open or close the drawer
     * If it's undefined, the url will be used as source to open the drawer or not
     */
    open?: boolean
    /**
     * Callback when viewer button is clicked
     */
    onToggle?: () => void
};

export type GenericViewerContext = {
    isInsideViewer?: boolean
    closeDrawer?: () => void
}
